export const CURRENCY = "GH\u20B5";

export interface GlAccount {
  id: number;
  code: string;
  name: string;
  type: "asset" | "liability" | "equity" | "revenue" | "expense";
  subtype: string | null;
  normal_balance: "debit" | "credit";
  parent_id: number | null;
  is_active: boolean;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GlJournalHeader {
  id: number;
  reference: string;
  entry_date: string;
  period_id: number;
  description: string | null;
  source: string;
  source_id: number | null;
  status: "draft" | "posted" | "reversed" | "void";
  posted_by: number | null;
  posted_at: string | null;
  reversed_by_journal_id: number | null;
  reverses_journal_id: number | null;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GlJournalLine {
  id: number;
  journal_id: number;
  line_no: number;
  account_id: number;
  account_code: string | null;
  account_name: string | null;
  debit: number;
  credit: number;
  description: string | null;
  reconciled_at: string | null;
}

export interface GlJournalListRow extends GlJournalHeader {
  total_debit: number;
  total_credit: number;
  line_count: number;
}

export interface GlJournalDetail extends GlJournalHeader {
  lines: GlJournalLine[];
  total_debit: number;
  total_credit: number;
  balanced: boolean;
  audit: Array<{
    id: number;
    action: string;
    actor_id: number | null;
    payload: string | null;
    created_at: string;
  }>;
}

export interface GlPeriod {
  id: number;
  year: number;
  month: number;
  start_date: string;
  end_date: string;
  status: "open" | "closed" | "locked";
  closed_by: number | null;
  closed_at: string | null;
}

export interface GlTrialBalanceRow {
  account_id: number;
  code: string;
  name: string;
  type: string;
  debit: number;
  credit: number;
}

export interface GlTrialBalanceResponse {
  as_of: string;
  rows: GlTrialBalanceRow[];
  total_debit: number;
  total_credit: number;
  balanced: boolean;
}

export interface GlLedgerLine {
  journal_id: number;
  reference: string;
  entry_date: string;
  journal_description: string | null;
  line_no: number;
  debit: number;
  credit: number;
  description: string | null;
  running_balance: number;
}

export interface GlLedgerResponse {
  account: GlAccount;
  from: string | null;
  to: string | null;
  opening_balance: number;
  closing_balance: number;
  lines: GlLedgerLine[];
}

export interface GlIncomeStatementResponse {
  from: string;
  to: string;
  revenues: Array<{ account: GlAccount; amount: number }>;
  expenses: Array<{ account: GlAccount; amount: number }>;
  total_revenue: number;
  total_expense: number;
  net_income: number;
}

export interface GlBalanceSheetResponse {
  as_of: string;
  assets: Array<{ account: GlAccount; amount: number }>;
  liabilities: Array<{ account: GlAccount; amount: number }>;
  equity: Array<{ account: GlAccount; amount: number }>;
  total_assets: number;
  total_liabilities: number;
  total_equity: number;
  ytd_earnings: number;
  total_liabilities_and_equity: number;
  balanced: boolean;
}

export interface GlAuditEntry {
  id: number;
  entity_type: string;
  entity_id: number;
  action: string;
  actor_id: number | null;
  actor_label: string | null;
  payload: string | null;
  created_at: string;
}

export interface GlOverviewResponse {
  kpis: {
    total_assets: number;
    total_liabilities: number;
    total_equity: number;
    net_income_period: number;
    unposted_journals: number;
    open_period: GlPeriod | null;
  };
  recent_journals: Array<{
    id: number;
    reference: string;
    entry_date: string;
    description: string | null;
    status: string;
    source: string;
    total_debit: number;
  }>;
  recent_audit: Array<{
    id: number;
    entity_type: string;
    entity_id: number;
    action: string;
    actor_label: string | null;
    created_at: string;
  }>;
}

export const ACCOUNT_TYPES: Array<{
  value: GlAccount["type"];
  label: string;
  normal: GlAccount["normal_balance"];
}> = [
  { value: "asset", label: "Asset", normal: "debit" },
  { value: "liability", label: "Liability", normal: "credit" },
  { value: "equity", label: "Equity", normal: "credit" },
  { value: "revenue", label: "Revenue", normal: "credit" },
  { value: "expense", label: "Expense", normal: "debit" },
];

export const TYPE_LABEL: Record<GlAccount["type"], string> = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  revenue: "Revenue",
  expense: "Expense",
};

export const JOURNAL_STATUS_BADGE: Record<string, string> = {
  draft:
    "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30",
  posted:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  reversed:
    "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  void: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
};

export const PERIOD_STATUS_BADGE: Record<string, string> = {
  open: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  closed:
    "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  locked:
    "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30",
};

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function monthName(month: number): string {
  return new Date(Date.UTC(2020, month - 1, 1)).toLocaleString(undefined, {
    month: "long",
  });
}

export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}
